import React from 'react';
import { Twitter, Linkedin, Github, Facebook, Instagram } from 'lucide-react';

const BRAND_DESCRIPTION =
  'Akwid Labs is a Sri Lanka-based technology company specializing in AI, Telecom, Data Engineering, Software Engineering, FinTech, XR & Gaming. We help startups and enterprises build scalable, secure, and future-ready systems.';

const SERVICES_LINKS = [
  { label: 'Success Stories', href: '/success-stories' },
  { label: 'AI & Data Engineering', href: '/ai-and-data' },
  { label: 'Telco Solutions', href: '/engineering/telco' },
  { label: 'FinTech Systems', href: '/engineering/fintech' },
  { label: 'XR & Gaming', href: '/engineering/xr-gaming' },
  { label: 'Software Engineering', href: '/engineering' },
];

const COMPANY_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'About Akwid', href: '/about' },
  { label: 'Careers', href: '/join-us' },
  { label: 'Privacy Policy', href: '/privacy-policy' },
  { label: 'Terms of Service', href: '/terms' },
  { label: 'Contact Us', href: '/contacts' },
];

const CONTACT_INFO = [
  { label: 'Email', value: 'hello@akwidlabs.com', href: 'mailto:hello@akwidlabs.com' },
  { label: 'Phone', value: '+94 76 911 4371', href: 'tel:+94769114371' },
];

const SOCIAL_LINKS: {
  href: string;
  label: string;
  Icon: React.ComponentType<any>;
}[] = [
  { href: 'https://twitter.com/akwidlabs', label: 'Twitter', Icon: Twitter },
  { href: 'https://linkedin.com/company/akwidlabs', label: 'LinkedIn', Icon: Linkedin },
  { href: 'https://github.com/orgs/Akwid-Labs', label: 'GitHub', Icon: Github },
  { href: 'https://facebook.com/akwidlabs', label: 'Facebook', Icon: Facebook },
  { href: 'https://instagram.com/akwid.labs', label: 'Instagram', Icon: Instagram },
];

function BrandSection() {
  return (
    <div className="space-y-6">
      <img src="/Images/Brand-Logo/3.png" alt="Akwid Labs Logo" className="h-20" />
      <p className="text-sm leading-relaxed text-gray-300">{BRAND_DESCRIPTION}</p>
    </div>
  );
}

function FooterMenu({ title, links }: { title: string; links: { label: string; href: string }[] }) {
  return (
    <div>
      <h3 className="mb-4 text-lg font-semibold text-purple-400">{title}</h3>
      <ul className="space-y-4 text-gray-300">
        {links.map(({ label, href }) => (
          <li key={label} className="group relative w-fit">
            <a href={href} className="transition-colors hover:text-purple-300">
              {label}
            </a>
            <span className="absolute -bottom-1 left-0 h-[2px] w-full origin-left scale-x-0 bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 transition-transform duration-300 group-hover:scale-x-100" />
          </li>
        ))}
      </ul>
    </div>
  );
}

function ContactSection() {
  return (
    <div>
      <h3 className="mb-4 text-lg font-semibold text-purple-400">Contact</h3>
      <ul className="space-y-4 text-sm text-gray-300">
        {CONTACT_INFO.map(({ label, value, href }) => (
          <li key={label}>
            {label}:{' '}
            <a href={href} className="ml-1 hover:text-purple-300">
              {value}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SocialSection() {
  return (
    <div>
      <h3 className="mb-4 text-lg font-semibold text-purple-400">Stay Connected</h3>
      <div className="flex space-x-4">
        {SOCIAL_LINKS.map(({ href, label, Icon }) => (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full bg-white/5 p-2 text-gray-200 backdrop-blur-xl transition-all hover:scale-110 hover:text-white"
            aria-label={label}
          >
            <Icon className="h-5 w-5" />
          </a>
        ))}
      </div>
    </div>
  );
}

function CopyrightSection() {
  return (
    <div className="mt-10 border-t border-gray-700 pt-6 text-center text-sm text-gray-200">
      © {new Date().getFullYear()} Akwid Labs (Pvt) Ltd. © All rights reserved.
      <div className="mt-2">
        <a href="/privacy-policy" className="hover:text-purple-300">
          Privacy Policy
        </a>
        <span className="mx-2">|</span>
        <a href="/terms" className="hover:text-purple-300">
          Terms of Service
        </a>
      </div>
      <p className="mt-2 text-xs text-gray-500">
        Version 3.1.1 — Designed & Engineered by Akwid Labs, Sri Lanka
      </p>
    </div>
  );
}

export default function Footer() {
  return (
    <footer className="relative bg-[#1a1625]/60 px-6 py-14 backdrop-blur-3xl">
      {/* Ambient gradient orbs */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-20 -top-24 h-72 w-72 rounded-full bg-gradient-to-r from-purple-600 via-pink-500 to-purple-700/30 blur-3xl" />
        <div className="absolute right-0 top-1/2 h-56 w-56 rounded-full bg-gradient-to-r from-pink-500 via-purple-500 to-purple-600/30 blur-3xl" />
      </div>

      <div className="relative mx-auto grid max-w-6xl grid-cols-1 gap-10 sm:grid-cols-2 md:grid-cols-5">
        <BrandSection />
        <FooterMenu title="Services" links={SERVICES_LINKS} />
        <FooterMenu title="Company" links={COMPANY_LINKS} />
        <ContactSection />
        <SocialSection />
      </div>

      <CopyrightSection />
    </footer>
  );
}
